import type { FareRuleParams } from 'api/flights'
import { getFareRules } from 'api/flights'
import Hr from 'components/Common/Hr'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { isEmptyObject } from 'lib/object/objectUtils'
import { rem } from 'polished'
import { Fragment, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

const StyledMarkdown = styled(MarkdownRender)`
  h5 {
    margin-top: ${rem(20)};
  }

  li {
    margin-top: 0;
  }
`

interface Props {
  urls?: Array<string>
  params?: FareRuleParams
}

function FlightFareRulesModalContent(props: Props) {
  const { urls, params } = props
  const intl = useIntl()
  const [fareRules, setFareRules] = useState<Array<any>>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    // reset our values each time it changes
    setError(undefined)
    setFareRules(undefined)
    if (urls) {
      // and refetch only when we have a value
      Promise.all(urls.map(url => getFareRules(url, params)))
        .then(fareRules => {
          const updatedFareRules = fareRules.filter(fareRule => !isEmptyObject(fareRule))

          if (updatedFareRules.length > 0) {
            setFareRules(updatedFareRules)
          } else {
            setError(intl.formatMessage({
              id: 'od1zEmtvSKhKxoGL5h8x',
              defaultMessage: 'Fare rules data failed to load',
              description: 'Error message when flight fare rules data could not be retrieved',
            }))
          }
        })
        .catch(() => {
          setError(intl.formatMessage({
            id: 'od1zEmtvSKhKxoGL5h8x',
            defaultMessage: 'Fare rules data failed to load',
            description: 'Error message when flight fare rules data could not be retrieved',
          }))
        })
    }
  }, [params, urls, intl])

  return <VerticalSpacer gap={20}>
    <Heading variant="heading5">
      <FormattedMessage
        id="ETjEYecxnRURUDIBEUhx"
        defaultMessage="Important"
        description="Section heading for important fare rules notice in the flights section"
      />
    </Heading>
    <BodyText variant="small">
      <FormattedMessage
        id="Foph28fsdiEEZ5dp9rzD"
        defaultMessage="Important: Fare rules may differ between flights on the same ticket. According to airline terms and conditions, the most restrictive rules apply to the entire ticket. Please check fare rules on all flight legs for accurate conditions."
        description="Warning about fare rules varying between flights on same ticket"
      />
    </BodyText>

    {!fareRules && !error && <LoadingIndicator inline />}
    {error && <MessageBanner kind="critical">{error}</MessageBanner>}
    {urls && fareRules?.map((rules, i) => (
      <Fragment key={i}>
        {i > 0 && <Hr size="lg" />}
        <StyledMarkdown fontSize="medium" content={rules} />
      </Fragment>
    ))}
  </VerticalSpacer>
}

export default FlightFareRulesModalContent
